import { deepClone } from '@cf-shared/deep-helpers';
import { Ingredient } from '@cf-shared/data-model/ingredient';

/**
 * Which of a meal's existing rows are tuned alongside a recipe's parts, when "Split &
 * Auto-Tune Foods" splits a recipe on the portion screen (df-69b7099c360e).
 *
 * The tuner is given the meal's rows and the recipe's parts as ONE set and sizes them all
 * against what the whole meal should contain. When the recipe being split is itself one of
 * those rows -- the user opened a recipe the meal already holds -- that row is about to be
 * replaced by its own parts, so it must not be in the set. Leaving it in counted the recipe
 * twice: once as itself and once as the foods it is made of, so the amounts the tuner chose
 * were fitted to a meal holding the recipe twice, and the row was then handed back and kept
 * beside its own parts.
 *
 * The rows come back as deep COPIES, because the caller writes the tuner's amounts into them
 * and the originals belong to the meal the user is still editing.
 *
 * @param existingIngredients The meal's rows as the page holds them. Anything that is not an
 *   array -- a caller with no meal behind it -- means there are no existing rows.
 * @param replacedIngredientIndex Where in that list the recipe being split sits, or
 *   `undefined` when the recipe is not one of the meal's rows at all. New mode, Log mode and
 *   the recent-meal confirmation all split a food the meal does not hold yet, so they pass
 *   `undefined` and every row is tuned.
 */
export function mealRowsTunedWithSplitParts(
  existingIngredients: Ingredient[] | undefined,
  replacedIngredientIndex: number | undefined,
): Ingredient[] {
  if (!Array.isArray(existingIngredients)) return [];

  return existingIngredients
    .filter((_, index) => index !== replacedIngredientIndex)
    .map(ingredient => deepClone(ingredient));
}
